import { useCallback, useState } from 'react'
import BackofficePanel, { PanelError, Row } from '../BackofficePanel'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import { clearOverride, getOverrides } from '../../../api/admin'
import type { OverrideView } from '../../../adminApi'

/**
 * Active overrides.
 *
 * Overrides are meant to be **temporary**, and a list nobody can see is a list nobody revisits —
 * a manual 3-1 set during one matchday and forgotten is a wrong result for the rest of the season.
 * One row each, the provider's value beside it, one click to clear.
 */

const OVERRIDES_POLL_MS = 30_000

export default function OverridesPanel({
  enabled,
  onChanged,
}: {
  enabled: boolean
  /** Fires after a clear so the panels showing this fixture re-read it. */
  onChanged: (message: string) => void
}) {
  const load = useCallback(() => getOverrides(), [])
  const poll = useAdminPoll(load, { enabled, intervalMs: OVERRIDES_POLL_MS })
  const [busy, setBusy] = useState<string | null>(null)

  const clear = async (override: OverrideView) => {
    setBusy(override.fixtureId)
    try {
      const result = await clearOverride(override.fixtureId)
      poll.refresh()
      onChanged(
        result.requiresRescore
          ? `Override cleared. GW${result.gameweek} is already scored — re-score it to apply.`
          : 'Override cleared — the provider is back in control of this fixture.'
      )
    } catch (failure) {
      onChanged(`Could not clear: ${failure instanceof Error ? failure.message : String(failure)}`)
    } finally {
      setBusy(null)
    }
  }

  const overrides = poll.data?.overrides ?? []

  return (
    <BackofficePanel
      title="Manual overrides"
      badge={poll.data ? String(overrides.length) : '…'}
      defaultOpen={overrides.length > 0}
      subtitle="A manual value sits beside the provider's, never on top of it. Clearing hands control straight back."
    >
      {poll.error !== null && <PanelError error={poll.error} />}
      {overrides.length === 0 ? (
        <p className="bo-empty">
          No overrides. Every fixture is showing exactly what the provider reported.
        </p>
      ) : (
        overrides.map((override) => (
          <div key={override.fixtureId} className="bo-match">
            <div className="bo-match__score">
              <span className="bo-mono">{override.fixtureId}</span>
              <span className="bo-tag bo-tag--manual">manual</span>
            </div>
            {/* Every field, with what the provider says beside it — the same rule as the sheet. */}
            {override.status !== null && (
              <Row
                label="status"
                value={`${override.status} (provider: ${override.provider?.status ?? '—'})`}
              />
            )}
            {(override.homeScore !== null || override.awayScore !== null) && (
              <Row
                label="score"
                value={`${override.homeScore ?? '—'}–${override.awayScore ?? '—'} (provider: ${
                  override.provider?.homeScore ?? '—'
                }–${override.provider?.awayScore ?? '—'})`}
              />
            )}
            <Row label="reason" value={override.reason} />
            <Row label="set by" value={`${override.setByEmail} · ${override.updatedAt}`} />
            <div className="bo-btn-row">
              <button
                type="button"
                className="bo-btn"
                disabled={busy === override.fixtureId}
                onClick={() => void clear(override)}
              >
                Restore to provider
              </button>
            </div>
          </div>
        ))
      )}
    </BackofficePanel>
  )
}
